/**
 * The contract both adapters implement. One interface, two implementations: the mock one that
 * ships today and the HTTP one that points at /api/v1 tomorrow.
 *
 * Author: Danish Khan
 */
import type {
  Conversation,
  Contact,
  Explain,
  Handoff,
  HandoffOutcome,
  HealthSummary,
  HomeSummary,
  InboxQuery,
  Lead,
  Message,
  MetricValue,
  Page,
  PipelineCard,
  PipelineStage,
  Product,
  Role,
  Session,
  User,
} from './types.ts';
import type { ThemeJson } from './theme.ts';

export interface Api {
  /** Which source this is. Pages never branch on it; `mediaUrl()` and the dev banner do. */
  readonly kind: 'mock' | 'http';

  login(email: string, password: string): Promise<Session>;
  logout(): Promise<void>;
  me(): Promise<Session | null>;
  theme(): Promise<ThemeJson>;

  metrics(names?: string[]): Promise<MetricValue[]>;
  home(): Promise<HomeSummary>;
  inbox(query: InboxQuery): Promise<Page<Conversation>>;
  messages(conversationId: string): Promise<Message[]>;
  lead(leadId: string): Promise<Lead>;
  explain(turnId: string): Promise<Explain>;

  sendMessage(leadId: string, text: string): Promise<Message>;
  addNote(leadId: string, body: string): Promise<Lead>;
  setStage(leadId: string, stage: string, reason: string): Promise<Lead>;

  /**
   * There is no `/leads/:id/takeover`, and no `steer`.
   *
   * Taking a thread over IS claiming its handoff: `POST /handoffs/:id/claim` writes
   * `handoff.takeovers` and mutes the agent for twenty minutes. Taking over a thread that has no
   * handoff is not built, and the composer says so rather than offering a button that 404s.
   * `convo.steer_notes` needs the turn packet to read it, which does not exist — so the steer box
   * is disabled in words rather than posting into a void.
   */
  handoffs(status?: 'open' | 'claimed' | 'closed' | 'all'): Promise<Handoff[]>;
  claimHandoff(id: string): Promise<Handoff>;
  releaseHandoff(id: string): Promise<Handoff>;
  closeHandoff(id: string, outcome: HandoffOutcome, reason: string): Promise<Handoff>;

  pipeline(): Promise<{ stages: PipelineStage[]; cards: PipelineCard[] }>;
  /**
   * `cursor` is declared, and that is the point.
   *
   * `GET /contacts` pages a hundred rows at a time and keysets on the contact id. Contacts.tsx has
   * always passed a cursor from `useInfiniteQuery`, but the parameter was not on this interface —
   * so the object spread suppressed the excess-property check, `httpApi.contacts` dropped it, and
   * every "Load more" re-fetched page one: duplicate React keys and a footer count that grew while
   * nothing new appeared.
   */
  contacts(query: { q?: string; consent?: 'all' | 'opted_out'; cursor?: string | null }): Promise<Page<Contact>>;

  products(query: { q?: string; category?: string }): Promise<Product[]>;
  product(id: string): Promise<Product>;
  setMediaFlags(mediaId: string, flags: { allowedSend?: boolean; featured?: boolean }): Promise<Product>;

  health(): Promise<HealthSummary>;
  people(): Promise<User[]>;
  /** Owner only. The generated password is readable exactly once — in the response. */
  addPerson(input: { email: string; name: string; role: Role }): Promise<{ user: User; password: string }>;
  /** Owner only. Deactivates the account and revokes every session it holds. */
  deactivatePerson(id: string): Promise<User>;
}

/** Thrown for a 4xx/5xx so ErrorState can show something a human can act on. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
